import { Injectable } from '@angular/core';
import { APIService } from './api.service';

/**
 * Shape of one entry from GET /api/subscription/options
 * ({ id, name, price, description, features[] } per tier — see
 * SubscriptionController#buildSubscriptionOptionResponse on the backend).
 * `price` is the tier's MONTHLY reference price only (or null if not
 * resolvable) — it is NOT sufficient to purchase with; use
 * getSchemeIdsForTier() to resolve the actual purchasable
 * subscriptionSchemeId(s) (monthly + annual) for a paid tier.
 */
export interface SubscriptionTierOption {
  id: number;
  name: string;
  price: number | null;
  description: string;
  features: string[];
}

/**
 * Purchasable scheme ids/prices for one paid tier, resolved via the same
 * api/product/package/list -> api/product/package/{packageId} lookup the
 * pre-existing Pro upsell flow already used (ProUpsellModalComponent
 * .fetchSubscriptionSchemes(), generalized here by package name so
 * Commissioner can reuse the identical mechanism instead of forking it).
 */
export interface TierSchemeIds {
  monthlySchemeId: number | null;
  monthlyPrice: number | null;
  annualSchemeId: number | null;
  annualPrice: number | null;
}

export interface PurchaseResult {
  success: boolean;
  /** PayPal approval URL to redirect to on success. */
  approvalUrl?: string;
  alreadySubscribed?: boolean;
  errorMessage?: string;
}

export interface DowngradeResult {
  success: boolean;
  errorMessage?: string;
}

/**
 * Shape of GET /api/subscription/my-subscription's Data - the caller's own
 * active individual-tier subscriber row (see SubscriptionController#getMySubscription).
 */
export interface MySubscription {
  subscriberId: number;
  subscriptionSchemeId: number;
  expirationDate: string | null;
  subscriptionStatusId: number;
}

/**
 * Display order mandated by conventions.md / phase-6-brief.md's Addendum:
 * Rookie, Commissioner, Pro (Pro is highest/most-popular despite having a
 * LOWER numeric subscription id than Commissioner - D1 in conventions.md).
 * Sorting by name here (rather than by the backend's numeric id) sidesteps
 * that id-vs-capability mismatch entirely instead of hardcoding id order.
 */
const TIER_DISPLAY_ORDER = ['Rookie', 'Commissioner', 'Pro'];

/**
 * SG-177 Phase 6: shared Angular service for the 3-tier (Rookie/
 * Commissioner/Pro) subscription overlay + My Account subscription
 * summary. Centralizes the API calls both ProUpsellModalComponent (the
 * subscription overlay) and MyAccountComponent need, so neither duplicates
 * the other's scheme-id-resolution / purchase / downgrade logic - "reuse
 * the existing subscription purchase plumbing, extend don't fork" per the
 * phase brief.
 *
 * No dedicated Angular "SubscriptionService" existed before this phase -
 * every subscription-related API call was inlined directly in components
 * (see buy-subscription.component.ts, the pre-Phase-6
 * pro-upsell-modal.component.ts). This service does not replace those
 * call sites' other behavior; it only centralizes the 3-tier
 * options/scheme-id/purchase/downgrade calls introduced by Phase 6.
 */
@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {

  constructor(private apiService: APIService) { }

  /**
   * GET /api/subscription/options - the 3 individual, user-selectable
   * tiers (Rookie, Commissioner, Pro; Enterprise is org-only and excluded
   * server-side). Result is always returned in the mandated display order
   * (Rookie, Commissioner, Pro), regardless of what order the backend
   * returns them in.
   */
  getSubscriptionOptions(callback: (tiers: SubscriptionTierOption[]) => void): void {
    this.apiService.doApiCall(
      (response, isError) => {
        // GET's built-in error path in APIService re-invokes this same
        // data callback with (null, true) on a non-token HTTP/network
        // failure; ignore that signal and let errorCallback below resolve
        // once instead of double-firing (matches
        // pro-subscription-validation.service.ts's established pattern).
        if (isError) {
          return;
        }
        if (response && response.StatusCode === 'SG001' && Array.isArray(response.Data)) {
          const tiers: SubscriptionTierOption[] = response.Data;
          tiers.sort((a, b) => TIER_DISPLAY_ORDER.indexOf(a.name) - TIER_DISPLAY_ORDER.indexOf(b.name));
          callback(tiers);
        } else {
          callback([]);
        }
      },
      'api/subscription/options',
      'GET',
      null,
      null,
      () => callback([])
    );
  }

  /**
   * Resolves the purchasable monthly/annual subscriptionSchemeId + price
   * for a paid tier by its tier name ('Commissioner' or 'Pro'), mirroring
   * ProUpsellModalComponent's pre-Phase-6 fetchSubscriptionSchemes():
   * api/product/package/list -> find the package whose name matches ->
   * api/product/package/{packageId} -> read subscriptionSchemeList,
   * splitting monthly (subscriptionPeriod === 1) from annual
   * (subscriptionPeriod >= 12).
   *
   * Package name matching (unavoidable frontend hardcoding - see
   * phase-6-report.md self-review): the Commissioner package is seeded as
   * exactly 'Commissioner' (Constants.PackageName.COMMISSIONER on the
   * backend); Pro's package is seeded as 'SG Pro'
   * (scripts/seed_sg_pro_subscription.sql) - there is no API that returns
   * "the package name for tier X" for the frontend to read live, so this
   * name mapping mirrors the same hardcoded assumption the pre-existing
   * Pro upsell code already made.
   */
  getSchemeIdsForTier(tierName: string, callback: (result: TierSchemeIds) => void): void {
    const empty: TierSchemeIds = { monthlySchemeId: null, monthlyPrice: null, annualSchemeId: null, annualPrice: null };
    const matcher = tierName === 'Pro' ? 'sg pro' : (tierName || '').toLowerCase();
    if (!matcher) {
      callback(empty);
      return;
    }

    this.apiService.doApiCall(
      (data, isError) => {
        // GET's built-in error path in APIService re-invokes this same data
        // callback with (null, true) on a non-token HTTP/network failure;
        // ignore that signal and let errorCallback below resolve once
        // instead of double-firing (same guard getSubscriptionOptions and
        // pro-subscription-validation.service.ts already apply — missing
        // here was the Important review finding this fixes).
        if (isError) {
          return;
        }
        if (data && data.Data) {
          const pkg = data.Data.find((p: any) => p.name && p.name.toLowerCase().includes(matcher));
          if (pkg) {
            this.apiService.doApiCall(
              (schemeData, innerIsError) => {
                if (innerIsError) {
                  return;
                }
                const result: TierSchemeIds = { ...empty };
                if (schemeData && schemeData.Data && schemeData.Data.subscriptionSchemeList) {
                  schemeData.Data.subscriptionSchemeList.forEach((scheme: any) => {
                    if (scheme.subscriptionPeriod === 1) {
                      result.monthlySchemeId = scheme.subscriptionSchemeId;
                      result.monthlyPrice = scheme.price;
                    } else if (scheme.subscriptionPeriod >= 12) {
                      result.annualSchemeId = scheme.subscriptionSchemeId;
                      result.annualPrice = scheme.price;
                    }
                  });
                }
                callback(result);
              },
              'api/product/package/' + pkg.packageId,
              'GET',
              null,
              null,
              () => callback(empty)
            );
          } else {
            callback(empty);
          }
        } else {
          callback(empty);
        }
      },
      'api/product/package/list?page=&limit=',
      'GET',
      null,
      null,
      () => callback(empty)
    );
  }

  /**
   * POST /api/subscription/billplan/create - starts a PayPal purchase for
   * the given subscriptionSchemeId, mirroring ProUpsellModalComponent's
   * pre-Phase-6 onUpgrade(). Works for ANY scheme id (Commissioner and Pro
   * alike) - the backend's Phase 2 fix
   * (SubscriptionServiceImpl#buySubscriptionExecutePlan) already resolves
   * the granted tier generically from the purchased scheme's package name,
   * so no tier-specific branching is needed here.
   *
   * Incidental fix vs. the pre-Phase-6 code this replaces: the old
   * onUpgrade() checked `data.StatusCode === 'SG152'` for "already
   * subscribed", but Constants.Response.ALREADY_SUBSCRIBED is actually
   * 'SG165' on the backend - 'SG152' would never match, so that branch was
   * dead code and the generic error message showed instead. Corrected here
   * since this method replaces that exact code path.
   */
  purchaseSubscription(schemeId: number, userId: number, callback: (result: PurchaseResult) => void): void {
    if (!schemeId || !userId) {
      callback({ success: false, errorMessage: 'Missing subscription plan or user information.' });
      return;
    }
    const payload = { subscriptionSchemeId: schemeId, userId: userId };
    this.apiService.doApiCall(
      (data) => {
        if (data && data.StatusCode === 'SG001') {
          callback({ success: true, approvalUrl: data.Data });
        } else if (data && data.StatusCode === 'SG165') {
          callback({ success: false, alreadySubscribed: true, errorMessage: 'You already have an active subscription for this plan.' });
        } else {
          callback({ success: false, errorMessage: (data && data.Message) || 'Payment initiation failed. Please try again.' });
        }
      },
      'api/subscription/billplan/create?requestedBy=' + userId,
      'POST',
      payload,
      null,
      () => callback({ success: false, errorMessage: 'Network error. Please try again.' })
    );
  }

  /**
   * GET /api/subscription/my-subscription - the caller's OWN active
   * individual-tier subscriber row (subscriberId, subscriptionSchemeId,
   * expirationDate, subscriptionStatusId), or null if they have none. Used
   * by My Account (Task 6.4) for the renewal date and the subscriberId a
   * downgrade needs.
   *
   * SECURITY (review fix, see phase-6-report.md "Fix round" section): this
   * replaces the phase's original approach, which filtered the ADMIN-facing
   * GET api/transaction/productsubscriber/list endpoint client-side by
   * username - that endpoint has no ownership check at all, so any
   * authenticated user could have requested ANY username's subscriber data
   * from the browser (an IDOR). The backend's new /my-subscription endpoint
   * resolves the caller's identity exclusively from their own validated
   * access-token header (same pattern as GET /contacts, Phase 4) and
   * returns only their own row - no identity param is sent by this method
   * at all, so there is nothing here for a caller to tamper with.
   */
  getMySubscription(callback: (subscriber: MySubscription | null) => void): void {
    this.apiService.doApiCall(
      (response, isError) => {
        if (isError) {
          return;
        }
        if (response && response.StatusCode === 'SG001') {
          callback(response.Data || null);
        } else {
          callback(null);
        }
      },
      'api/subscription/my-subscription',
      'GET',
      null,
      null,
      () => callback(null)
    );
  }

  /**
   * PUT /api/subscription/downgrade - downgrades an active Commissioner or
   * Pro subscriber back to Rookie. Pre-existing backend endpoint (used by
   * no frontend caller before this phase); requires the subscriberId of
   * the active row (see getMySubscription above). The backend now verifies
   * server-side that subscriberId belongs to the token-authenticated caller
   * (SG-177 Phase 6 fix round - was previously an IDOR: any authenticated
   * caller could downgrade any other user's subscription by id) - this
   * method still only ever sources subscriberId from getMySubscription's
   * response, never a caller-editable value, but the ownership guarantee
   * itself lives server-side where it can't be bypassed by a modified
   * client.
   */
  downgradeToRookie(subscriberId: number, userId: number, callback: (result: DowngradeResult) => void): void {
    if (!subscriberId || !userId) {
      callback({ success: false, errorMessage: 'Missing subscription information.' });
      return;
    }
    const payload = { subscriberId: subscriberId };
    this.apiService.doApiCall(
      (data) => {
        if (data && data.StatusCode === 'SG001') {
          callback({ success: true });
        } else {
          callback({ success: false, errorMessage: (data && data.Message) || 'Downgrade failed. Please try again.' });
        }
      },
      'api/subscription/downgrade?requestedBy=' + userId,
      'PUT',
      payload,
      null,
      () => callback({ success: false, errorMessage: 'Network error. Please try again.' })
    );
  }
}
